import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { X, Zap, Layers, Globe, AlertTriangle, Check, ShieldCheck, Timer, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useSession, useSessionStart, useSessionEnd, useSessionModifyWorkers, useWorkers } from '@/hooks/useApi'

// Renamed to "Worker Lock" in the UI, per direct agreement -- "session" was
// colliding with the unrelated Tier 2/3 override windows and with ordinary
// conversation-session meaning. Display-text rename only, originally --
// but the underlying mechanism has since been deliberately revised
// (2026-08-26, direct instruction): scope enforcement moved off
// chat_sessions onto a dedicated, session-independent KV key mirroring
// the Tier 2/3 pattern exactly, and starting/switching/ending a lock now
// requires a live TOTP code, matching the same "not something an AI can
// trigger by itself" standard already applied to Tier 2/3. This file is
// being updated to match -- hook/API names stay the same, but start/end
// now require a totpCode, and start also requires hours (bounded lifetime).
const SESSION_TYPES = [
  { value: 'single', label: 'Single Worker', icon: Zap, desc: 'Lock to one specific worker' },
  { value: 'orchestrate', label: 'Orchestrate', icon: Layers, desc: 'Select multiple workers across any ecosystem' },
  { value: 'platform', label: 'Platform', icon: Globe, desc: 'All workers — unrestricted access' },
] as const

function WorkerLock3DIcon({ active = false }: { active?: boolean }) {
  return (
    <svg className={`ops-worker-lock-3d-icon ${active ? 'is-active' : ''}`} viewBox="0 0 34 38" aria-hidden="true">
      <defs>
        <linearGradient id={active ? 'opsLockBodyActive' : 'opsLockBodyIdle'} x1="6" y1="7" x2="28" y2="34" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor={active ? '#e7fbff' : '#eef4ff'} />
          <stop offset=".25" stopColor={active ? '#6be3ff' : '#a8c4f2'} />
          <stop offset=".58" stopColor={active ? '#278fd7' : '#5f80ba'} />
          <stop offset="1" stopColor={active ? '#174f8e' : '#314d80'} />
        </linearGradient>
        <linearGradient id={active ? 'opsLockShackleActive' : 'opsLockShackleIdle'} x1="10" y1="3" x2="24" y2="19" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#f8fdff" />
          <stop offset=".42" stopColor={active ? '#9cecff' : '#cbdcff'} />
          <stop offset="1" stopColor={active ? '#347fc0' : '#617eae'} />
        </linearGradient>
        <filter id={active ? 'opsLockShadowActive' : 'opsLockShadowIdle'} x="-45%" y="-35%" width="190%" height="200%">
          <feDropShadow dx="0" dy="3" stdDeviation="2" floodColor={active ? '#0a5f91' : '#1d365d'} floodOpacity=".5" />
          {active && <feDropShadow dx="0" dy="0" stdDeviation="2.2" floodColor="#39d8ff" floodOpacity=".28" />}
        </filter>
      </defs>
      <g filter={`url(#${active ? 'opsLockShadowActive' : 'opsLockShadowIdle'})`}>
        <path d="M10.2 16v-4.2C10.2 6.4 13.1 3 17 3s6.8 3.4 6.8 8.8V16h-3.3v-4.1c0-3.2-1.3-5.4-3.5-5.4s-3.5 2.2-3.5 5.4V16h-3.3Z" fill={`url(#${active ? 'opsLockShackleActive' : 'opsLockShackleIdle'})`} stroke="#f7fbff" strokeOpacity=".68" strokeWidth=".8" />
        <rect x="5.2" y="14" width="23.6" height="20.2" rx="6" fill={`url(#${active ? 'opsLockBodyActive' : 'opsLockBodyIdle'})`} stroke="#eaf8ff" strokeOpacity=".78" strokeWidth="1" />
        <path d="M8.8 17.3c4.8-1.7 11.9-2 16.7-.5" fill="none" stroke="#fff" strokeOpacity=".5" strokeWidth="1" strokeLinecap="round" />
        <circle cx="17" cy="23.2" r="2.5" fill="#eefbff" stroke="#315f94" strokeOpacity=".55" strokeWidth=".7" />
        <path d="M17 25.3v4.1" stroke="#eefbff" strokeWidth="2" strokeLinecap="round" />
      </g>
    </svg>
  )
}

export function SessionManager() {
  const { data: sessionData, isLoading } = useSession()
  const { data: workersData } = useWorkers()
  const startSession = useSessionStart()

  const [showPicker, setShowPicker] = useState(false)
  const [step, setStep] = useState(1)
  const [selectedType, setSelectedType] = useState<string>('')
  const [selectedWorker, setSelectedWorker] = useState<string>('')
  const [selectedWorkers, setSelectedWorkers] = useState<string[]>([])
  const [platformReason, setPlatformReason] = useState('')
  const [hours, setHours] = useState('4')
  const [totpCode, setTotpCode] = useState('')

  if (isLoading) return null

  const isActive = sessionData?.active

  const resetPicker = () => {
    setStep(1)
    setSelectedType('')
    setSelectedWorker('')
    setSelectedWorkers([])
    setPlatformReason('')
    setHours('4')
    setTotpCode('')
  }

  const ecosystems = workersData?.ecosystems ?? {}
  const ecosystemLabels = workersData?.ecosystem_labels ?? {}
  const ecosystemEntries = Object.entries(ecosystems).filter(([, workers]) => workers.length > 0)
  const ecosystemLabel = (key: string) => ecosystemLabels[key] || key

  const toggleWorker = (name: string) => {
    setSelectedWorkers(prev =>
      prev.includes(name) ? prev.filter(w => w !== name) : [...prev, name]
    )
  }

  const hoursNum = Number(hours)
  const totpValid = /^\d{6}$/.test(totpCode)
  const hoursValid = hoursNum > 0 && hoursNum <= 24

  const handleStart = () => {
    startSession.mutate({
      session_type: selectedType,
      workers: selectedType === 'single'
        ? [selectedWorker]
        : selectedType === 'orchestrate'
        ? selectedWorkers
        : null,
      reason: selectedType === 'platform' ? platformReason : undefined,
      hours: hoursNum,
      totpCode,
    }, {
      onSuccess: () => {
        // Close the picker on genuine success rather than leaving it open
        // with a stale "Start Worker Lock" button -- the active lock now
        // shows via the normal SessionPill, which is where End already
        // lives, so there's no need for this modal to offer it too.
        setShowPicker(false)
        resetPicker()
      },
    })
  }

  if (!isActive && !showPicker) {
    return (
      <button
        onClick={() => setShowPicker(true)}
        className="ops-worker-lock-trigger ops-worker-lock-trigger-inactive"
        aria-label="Worker Lock — no active session"
        title="Start Worker Lock"
      >
        <WorkerLock3DIcon />
        <span className="ops-worker-lock-trigger-copy">
          <span className="ops-worker-lock-trigger-title">Worker Lock</span>
          <span className="ops-worker-lock-trigger-subtitle">No active session</span>
        </span>
      </button>
    )
  }

  if (showPicker) {
    return createPortal(
      <div className="ops-worker-lock-overlay fixed inset-0 z-50 flex items-center justify-center p-4">
        <Card className="ops-worker-lock-start-card w-full max-w-lg border-border p-6">
          <div className="ops-worker-lock-panel-header">
            <div className="ops-worker-lock-panel-heading">
              <div className="ops-worker-lock-icon-well"><WorkerLock3DIcon /></div>
              <div>
                <div className="ops-worker-lock-kicker">Controlled platform access</div>
                <h2 className="ops-worker-lock-panel-title">Start Worker Lock</h2>
                <p className="ops-worker-lock-panel-subtitle">Choose the scope, lifetime and workers for this controlled session.</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => { setShowPicker(false); resetPicker() }} className="ops-worker-lock-close">
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Step 1 — choose scope mode */}
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-sm text-text-muted mb-4">Choose scope:</p>
              {SESSION_TYPES.map(st => (
                <button
                  key={st.value}
                  onClick={() => { setSelectedType(st.value); setStep(2) }}
                  className="ops-worker-lock-scope-option w-full flex items-center gap-3 p-3 rounded-xl border border-border transition-colors text-left"
                >
                  <st.icon className="h-5 w-5 text-accent shrink-0" />
                  <div>
                    <div className="font-medium text-text-primary">{st.label}</div>
                    <div className="text-xs text-text-muted">{st.desc}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Step 2 — Single: pick one worker */}
          {step === 2 && selectedType === 'single' && (
            <div className="space-y-3">
              <p className="text-sm text-text-muted mb-2">Select worker:</p>
              <div className="max-h-72 overflow-y-auto space-y-3">
                {ecosystemEntries.map(([eco, workers]) => (
                  <div key={eco}>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-text-muted mb-1 px-1">{ecosystemLabel(eco)}</div>
                    {(workers as { name: string; status: string }[]).map(w => (
                      <button
                        key={w.name}
                        onClick={() => { setSelectedWorker(w.name); setStep(3) }}
                        className="w-full flex items-center justify-between px-2 py-2 rounded hover:bg-accent/10 text-left min-h-[44px]"
                      >
                        <span className="text-sm text-text-primary">{w.name}</span>
                        <Badge
                          variant={w.status === 'healthy' ? 'success' : w.status === 'internal' ? 'muted' : 'danger'}
                          className="text-[10px]"
                        >
                          {w.status}
                        </Badge>
                      </button>
                    ))}
                  </div>
                ))}
                {ecosystemEntries.length === 0 && (
                  <div className="text-sm text-text-muted text-center py-4">Loading workers...</div>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={() => setStep(1)}>Back</Button>
            </div>
          )}

          {/* Step 2 — Orchestrate: multi-select workers */}
          {step === 2 && selectedType === 'orchestrate' && (
            <div className="space-y-3">
              <p className="text-sm text-text-muted mb-2">Select workers ({selectedWorkers.length} chosen):</p>
              <div className="max-h-72 overflow-y-auto space-y-3">
                {ecosystemEntries.map(([eco, workers]) => (
                  <div key={eco}>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-text-muted mb-1 px-1">{ecosystemLabel(eco)}</div>
                    {(workers as { name: string; status: string }[]).map(w => (
                      <label
                        key={w.name}
                        className="flex items-center gap-3 px-2 py-2 rounded hover:bg-accent/10 cursor-pointer min-h-[44px]"
                      >
                        <input
                          type="checkbox"
                          checked={selectedWorkers.includes(w.name)}
                          onChange={() => toggleWorker(w.name)}
                          className="h-4 w-4 accent-[var(--color-accent)] shrink-0"
                        />
                        <span className="text-sm text-text-primary flex-1">{w.name}</span>
                        <Badge
                          variant={w.status === 'healthy' ? 'success' : w.status === 'internal' ? 'muted' : 'danger'}
                          className="text-[10px]"
                        >
                          {w.status}
                        </Badge>
                      </label>
                    ))}
                  </div>
                ))}
                {ecosystemEntries.length === 0 && (
                  <div className="text-sm text-text-muted text-center py-4">Loading workers...</div>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setStep(1)}>Back</Button>
                <Button
                  size="sm"
                  disabled={selectedWorkers.length < 2}
                  onClick={() => setStep(3)}
                >
                  Continue ({selectedWorkers.length} selected)
                </Button>
              </div>
            </div>
          )}

          {/* Step 2 — Platform: reason input */}
          {step === 2 && selectedType === 'platform' && (
            <div className="space-y-3">
              <div className="flex items-start gap-2 p-3 rounded-lg bg-warning/10 border border-warning/30">
                <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                <div className="text-sm text-warning">
                  Platform locks grant access to ALL workers. Provide a reason to continue.
                </div>
              </div>
              <input
                type="text"
                placeholder="Reason for platform lock..."
                value={platformReason}
                onChange={e => setPlatformReason(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent"
              />
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setStep(1)}>Back</Button>
                <Button
                  size="sm"
                  disabled={!platformReason.trim()}
                  onClick={() => setStep(3)}
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* Step 3 — Confirm */}
          {step === 3 && (
            <div className="space-y-4">
              <p className="text-sm text-text-muted">Confirm worker lock:</p>
              <div className="ops-worker-lock-summary p-3 rounded-xl border border-border space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-text-muted">Type</span>
                  <span className="text-text-primary font-medium capitalize">{selectedType}</span>
                </div>
                {selectedType === 'single' && (
                  <div className="flex justify-between text-sm">
                    <span className="text-text-muted">Worker</span>
                    <span className="text-text-primary font-medium">{selectedWorker}</span>
                  </div>
                )}
                {selectedType === 'orchestrate' && (
                  <div className="text-sm">
                    <span className="text-text-muted block mb-1">Workers ({selectedWorkers.length})</span>
                    <div className="flex flex-wrap gap-1">
                      {selectedWorkers.map(w => (
                        <Badge key={w} variant="outline" className="text-[10px]">{w}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {selectedType === 'platform' && (
                  <div className="flex justify-between text-sm">
                    <span className="text-text-muted">Reason</span>
                    <span className="text-text-primary font-medium truncate max-w-[60%]">{platformReason}</span>
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs text-text-muted block mb-1">Hours (max 24) — locks must have a bounded lifetime</label>
                <input
                  type="number"
                  min={1}
                  max={24}
                  value={hours}
                  onChange={e => setHours(e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:border-accent"
                />
              </div>
              <div>
                <label className="text-xs text-text-muted block mb-1">Live 6-digit code from your authenticator — same as login, same as Tier 2/3</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  value={totpCode}
                  onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-lg tracking-[0.3em] text-center text-text-primary placeholder:text-text-muted placeholder:tracking-normal focus:outline-none focus:border-accent"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" size="sm" onClick={() => setStep(2)}>Back</Button>
                <Button size="sm" onClick={handleStart} disabled={startSession.isPending || !totpValid || !hoursValid}>
                  <Check className="h-4 w-4 mr-1" />
                  {startSession.isPending ? 'Starting...' : 'Start Worker Lock'}
                </Button>
              </div>
              {startSession.isError && (
                <p className="text-xs text-danger mt-2 break-all">
                  {JSON.stringify((startSession.error as Error)?.message || startSession.error)}
                </p>
              )}
              {startSession.isSuccess && (
                <p className="text-xs text-success mt-2">Worker lock started successfully</p>
              )}
            </div>
          )}
        </Card>
      </div>,
      document.body
    )
  }

  return null
}

export function SessionPill() {
  const { data: sessionData } = useSession()
  const { data: workersData } = useWorkers()
  const endSession = useSessionEnd()
  const modifyWorkers = useSessionModifyWorkers()
  const [showDetails, setShowDetails] = useState(false)
  const [showManageWorkers, setShowManageWorkers] = useState(false)
  const [selectedToAdd, setSelectedToAdd] = useState<string[]>([])
  const [removeWorkerName, setRemoveWorkerName] = useState('')
  const [manageTotpCode, setManageTotpCode] = useState('')
  const [showEndTotp, setShowEndTotp] = useState(false)
  const [endTotpCode, setEndTotpCode] = useState('')
  const [nowMs, setNowMs] = useState<number | null>(null)

  useEffect(() => {
    const tick = () => setNowMs(Date.now())
    tick()
    const timer = window.setInterval(tick, 1_000)
    return () => window.clearInterval(timer)
  }, [])

  if (!sessionData?.active || !sessionData.session) return null

  const s = sessionData.session
  const elapsedSeconds = nowMs == null ? 0 : Math.max(0, Math.floor((nowMs - new Date(s.started_at).getTime()) / 1000))
  const elapsedHours = Math.floor(elapsedSeconds / 3600)
  const elapsedMinutes = Math.floor((elapsedSeconds % 3600) / 60)
  const elapsedRemainder = elapsedSeconds % 60
  const elapsedClock = [elapsedHours, elapsedMinutes, elapsedRemainder].map(v => String(v).padStart(2, '0')).join(':')
  const lockLabel = s.session_type === 'platform'
    ? 'Platform Lock'
    : s.session_type === 'orchestrate'
      ? 'Orchestrate Lock'
      : 'Single Worker Lock'
  const ecosystems = workersData?.ecosystems ?? {}
  const ecosystemLabels = workersData?.ecosystem_labels ?? {}
  const ecosystemLabel = (key: string) => ecosystemLabels[key] || key
  const addableEcosystemEntries = Object.entries(ecosystems)
    .map(([eco, workers]) => [eco, (workers as { name: string }[]).filter(w => !s.scope.includes(w.name))] as const)
    .filter(([, workers]) => workers.length > 0)

  const toggleAddWorker = (name: string) => {
    setSelectedToAdd(prev =>
      prev.includes(name) ? prev.filter(w => w !== name) : [...prev, name]
    )
  }

  const handleAddSelected = async () => {
    for (const worker of selectedToAdd) {
      await modifyWorkers.mutateAsync({ action: 'add', worker, totpCode: manageTotpCode })
    }
    setSelectedToAdd([])
  }

  return (
    <>
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="ops-worker-lock-trigger ops-worker-lock-trigger-active"
        aria-label={`${lockLabel} — active for ${elapsedClock}`}
        aria-expanded={showDetails}
        title={`${lockLabel} · ${s.scope.length} workers in scope`}
      >
        <span className="ops-worker-lock-live-dot" />
        <WorkerLock3DIcon active />
        <span className="ops-worker-lock-trigger-copy">
          <span className="ops-worker-lock-trigger-title">{lockLabel}</span>
          <span className="ops-worker-lock-trigger-subtitle ops-worker-lock-elapsed">{elapsedClock}</span>
        </span>
        <span className="ops-worker-lock-scope-count">{s.scope.length}w</span>
      </button>

      {showDetails && createPortal(
        <>
          <div className="ops-worker-lock-detail-backdrop fixed inset-0 z-[99998]" onClick={() => setShowDetails(false)} />
          <div className="ops-worker-lock-detail-shell fixed z-[99999]">
            <Card className="ops-worker-lock-detail-card border-border">
              <div className="ops-worker-lock-panel-header">
                <div className="ops-worker-lock-panel-heading">
                  <div className="ops-worker-lock-icon-well is-active"><WorkerLock3DIcon active /></div>
                  <div className="min-w-0">
                    <div className="ops-worker-lock-kicker"><span className="ops-worker-lock-live-dot" /> Active session</div>
                    <h3 className="ops-worker-lock-panel-title">{lockLabel}</h3>
                    <p className="ops-worker-lock-panel-subtitle">Restricting platform access to the selected workers.</p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setShowDetails(false)} className="ops-worker-lock-close">
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="ops-worker-lock-summary-grid">
                <div className="ops-worker-lock-stat-card">
                  <div className="ops-worker-lock-stat-icon"><Timer className="h-4 w-4" /></div>
                  <div>
                    <div className="ops-worker-lock-stat-label">Active session</div>
                    <div className="ops-worker-lock-stat-value ops-worker-lock-stat-clock">{elapsedClock}</div>
                    <div className="ops-worker-lock-stat-note">Started {new Date(s.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                  </div>
                </div>
                <div className="ops-worker-lock-stat-card">
                  <div className="ops-worker-lock-stat-icon"><Users className="h-4 w-4" /></div>
                  <div>
                    <div className="ops-worker-lock-stat-label">Workers in scope</div>
                    <div className="ops-worker-lock-stat-value">{s.scope.length}</div>
                    <div className="ops-worker-lock-stat-note capitalize">{s.session_type} scope</div>
                  </div>
                </div>
              </div>

              <div className="ops-worker-lock-scope-section">
                <div className="ops-worker-lock-section-title-row">
                  <div>
                    <div className="ops-worker-lock-section-title">In scope</div>
                    <div className="ops-worker-lock-section-subtitle">{s.scope.length} worker{s.scope.length === 1 ? '' : 's'} currently authorised by this lock.</div>
                  </div>
                  <ShieldCheck className="h-5 w-5 text-accent" />
                </div>
                <div className="ops-worker-lock-chip-cloud">
                  {s.scope.map((w: string) => (
                    <span key={w} className="ops-worker-lock-chip">{w}</span>
                  ))}
                </div>
              </div>

              <div className="ops-worker-lock-session-meta">
                <div><span>Session</span><strong title={s.session_id}>{s.session_id}</strong></div>
                {s.out_of_scope && s.out_of_scope.length > 0 && <div><span>Out of scope</span><strong>{s.out_of_scope.length}</strong></div>}
                {s.changes_this_session && s.changes_this_session.length > 0 && <div><span>Changes</span><strong>{s.changes_this_session.length}</strong></div>}
                {s.pending_items && s.pending_items.length > 0 && <div><span>Pending</span><strong className="text-warning">{s.pending_items.length}</strong></div>}
              </div>

              <div className="ops-worker-lock-panel-actions">
                {s.session_type === 'orchestrate' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setShowDetails(false); setShowManageWorkers(true) }}
                    className="ops-worker-lock-secondary-action"
                  >
                    Manage Workers
                  </Button>
                )}
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => { setShowDetails(false); setShowEndTotp(true) }}
                  disabled={endSession.isPending}
                  className="ops-worker-lock-end-action"
                >
                  End Lock
                </Button>
              </div>
            </Card>
          </div>
        </>,
        document.body
      )}

      {showManageWorkers && createPortal(
        <div className="ops-worker-lock-overlay fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <Card className="ops-worker-lock-modal-card w-full max-w-sm border-border p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-semibold text-text-primary">Manage Worker Lock list</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => { setShowManageWorkers(false); setSelectedToAdd([]); setRemoveWorkerName(''); setManageTotpCode('') }}
                className="h-6 w-6"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
            <p className="text-xs text-text-muted mb-3">Currently locked ({s.scope.length}):</p>
            <div className="flex flex-wrap gap-1 mb-4">
              {s.scope.map((w: string) => (
                <Badge key={w} variant="outline" className="text-[10px]">{w}</Badge>
              ))}
            </div>

            <div className="mb-4">
              <label className="text-xs text-text-muted block mb-1">Add workers — select or deselect ({selectedToAdd.length} chosen)</label>
              <div className="max-h-48 overflow-y-auto space-y-3 border border-border rounded-lg p-2 mb-2">
                {addableEcosystemEntries.map(([eco, workers]) => (
                  <div key={eco}>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-text-muted mb-1 px-1">{ecosystemLabel(eco)}</div>
                    {(workers as { name: string; status: string }[]).map(w => (
                      <label
                        key={w.name}
                        className="flex items-center gap-3 px-2 py-2 rounded hover:bg-accent/10 cursor-pointer min-h-[44px]"
                      >
                        <input
                          type="checkbox"
                          checked={selectedToAdd.includes(w.name)}
                          onChange={() => toggleAddWorker(w.name)}
                          className="h-4 w-4 accent-[var(--color-accent)] shrink-0"
                        />
                        <span className="text-sm text-text-primary flex-1">{w.name}</span>
                        <Badge
                          variant={w.status === 'healthy' ? 'success' : w.status === 'internal' ? 'muted' : 'danger'}
                          className="text-[10px]"
                        >
                          {w.status}
                        </Badge>
                      </label>
                    ))}
                  </div>
                ))}
                {addableEcosystemEntries.length === 0 && (
                  <div className="text-sm text-text-muted text-center py-4">Every known worker is already in this lock.</div>
                )}
              </div>
              <Button
                size="sm"
                onClick={handleAddSelected}
                disabled={modifyWorkers.isPending || selectedToAdd.length === 0 || !/^\d{6}$/.test(manageTotpCode)}
                className="text-xs w-full"
              >
                {modifyWorkers.isPending ? 'Working...' : `Add ${selectedToAdd.length || ''} selected`.trim()}
              </Button>
            </div>

            <div className="mb-4">
              <label className="text-xs text-text-muted block mb-1">Remove a worker</label>
              <select
                value={removeWorkerName}
                onChange={e => setRemoveWorkerName(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:border-accent mb-2"
                disabled={s.scope.length <= 2}
              >
                <option value="">Select a worker...</option>
                {s.scope.map((w: string) => <option key={w} value={w}>{w}</option>)}
              </select>
              {s.scope.length <= 2 ? (
                <p className="text-xs text-text-muted">Orchestrate locks need at least two workers — end the lock instead of removing this one.</p>
              ) : (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    modifyWorkers.mutate({ action: 'remove', worker: removeWorkerName, totpCode: manageTotpCode }, {
                      onSuccess: () => setRemoveWorkerName(''),
                    })
                  }}
                  disabled={modifyWorkers.isPending || !removeWorkerName || !/^\d{6}$/.test(manageTotpCode)}
                  className="text-xs w-full"
                >
                  {modifyWorkers.isPending ? 'Working...' : 'Remove from lock'}
                </Button>
              )}
            </div>

            <label className="text-xs text-text-muted block mb-1">Live 6-digit code — required for either action above</label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="123456"
              value={manageTotpCode}
              onChange={e => setManageTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-lg tracking-[0.3em] text-center text-text-primary placeholder:text-text-muted placeholder:tracking-normal focus:outline-none focus:border-accent mb-3"
            />
            {modifyWorkers.isError && (
              <p className="text-xs text-danger mb-3 break-all">
                {JSON.stringify((modifyWorkers.error as Error)?.message || modifyWorkers.error)}
              </p>
            )}
            {modifyWorkers.isSuccess && (
              <p className="text-xs text-success mb-3">{modifyWorkers.data?.message}</p>
            )}
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setShowManageWorkers(false); setSelectedToAdd([]); setRemoveWorkerName(''); setManageTotpCode('') }}
                className="text-xs"
              >
                Close
              </Button>
            </div>
          </Card>
        </div>,
        document.body
      )}

      {showEndTotp && createPortal(
        <div className="ops-worker-lock-overlay fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <Card className="ops-worker-lock-modal-card w-full max-w-sm border-border p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-semibold text-text-primary">Confirm end lock</h3>
              <Button variant="ghost" size="icon" onClick={() => { setShowEndTotp(false); setEndTotpCode('') }} className="h-6 w-6">
                <X className="h-3 w-3" />
              </Button>
            </div>
            <p className="text-xs text-text-muted mb-4">Ending or switching a Worker Lock requires a live code, the same standard as Tier 2/3 — this can&apos;t be triggered by anything other than you entering it here.</p>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="123456"
              value={endTotpCode}
              onChange={e => setEndTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-lg tracking-[0.3em] text-center text-text-primary placeholder:text-text-muted placeholder:tracking-normal focus:outline-none focus:border-accent mb-3"
            />
            {endSession.isError && (
              <p className="text-xs text-danger mb-3 break-all">
                {JSON.stringify((endSession.error as Error)?.message || endSession.error)}
              </p>
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => { setShowEndTotp(false); setEndTotpCode('') }} className="text-xs">Cancel</Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  endSession.mutate(endTotpCode, {
                    onSuccess: () => { setShowEndTotp(false); setEndTotpCode('') },
                  })
                }}
                disabled={endSession.isPending || !/^\d{6}$/.test(endTotpCode)}
                className="text-xs"
              >
                {endSession.isPending ? 'Ending...' : 'Confirm end lock'}
              </Button>
            </div>
          </Card>
        </div>,
        document.body
      )}
    </>
  )
}
